import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import { formatEuro, formatNumber, formatPercent } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Landmark, Building, DoorOpen, TrendingUp, Coins, AlertTriangle } from "lucide-react";
import { BUILDING_TYPE_LABELS, OCCUPANCY_STATUS_LABELS } from "@/constants/patrimoine";
import SmartLoader from "@/components/common/SmartLoader";

export default function PatrimoinePage() {
  const [companies, setCompanies] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState({ company_id: "any", building_id: "any", building_type: "any", occupancy_status: "any" });

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "any") p.set(k, v); });
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/buildings")])
      .then(([c, b]) => { setCompanies(c.data.items || []); setBuildings(b.data.items || []); })
      .catch(() => {});
  }, []);

  const loadDashboard = () => {
    setLoadError(null);
    api.get(`/patrimoine/dashboard${query}`)
      .then(({ data }) => setData(data))
      .catch((e) => setLoadError(e.response?.data?.detail || "Impossible de charger la vue patrimoine."));
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  if (!data || loadError) return <SmartLoader label="Chargement du patrimoine…" onRetry={loadDashboard} error={loadError} />;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Consolidation"
        title="Patrimoine"
        description="Vue consolidée de vos bâtiments et lots. Filtrez pour zoomer sur une SCI, un bâtiment ou un type."
      />

      {/* Filters */}
      <div className="card-elevated p-4 lg:p-5 mb-8">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FilterSelect label="Société" value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}
                        options={[["any", "Toutes"], ...companies.map((c) => [c.id, c.denomination])]}
                        testId="patrimoine-filter-company" />
          <FilterSelect label="Bâtiment" value={filters.building_id} onValueChange={(v) => setFilters({ ...filters, building_id: v })}
                        options={[["any", "Tous"], ...buildings.map((b) => [b.id, b.name])]}
                        testId="patrimoine-filter-building" />
          <FilterSelect label="Type" value={filters.building_type} onValueChange={(v) => setFilters({ ...filters, building_type: v })}
                        options={[["any", "Tous"], ...Object.entries(BUILDING_TYPE_LABELS)]}
                        testId="patrimoine-filter-type" />
          <FilterSelect label="Occupation" value={filters.occupancy_status} onValueChange={(v) => setFilters({ ...filters, occupancy_status: v })}
                        options={[["any", "Tous"], ...Object.entries(OCCUPANCY_STATUS_LABELS)]}
                        testId="patrimoine-filter-occupancy" />
        </div>
      </div>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <KpiCard name="patrimoine-value" label="Valeur estimée" icon={Landmark} tone="accent"
                 value={formatEuro(data.values.estimated_value)}
                 deltaLabel={`Acquis. ${formatEuro(data.values.acquisition_cost)}`} />
        <KpiCard name="patrimoine-buildings" label="Bâtiments" icon={Building}
                 value={formatNumber(data.counts.buildings)} />
        <KpiCard name="patrimoine-lots" label="Lots" icon={DoorOpen}
                 value={formatNumber(data.counts.lots)}
                 deltaLabel={`${data.counts.occupied_lots} occupés · ${data.counts.vacant_lots} vacants`} />
        <KpiCard name="patrimoine-occupancy" label="Taux d'occupation" icon={TrendingUp}
                 value={formatPercent(data.occupancy.rate_pct)} />
      </section>

      <section className="grid gap-6 lg:grid-cols-3 mb-8">
        <div className="card-elevated p-6">
          <div className="text-overline">Surface</div>
          <div className="mt-2 font-serif text-3xl">{formatNumber(Math.round(data.surface.total_m2))} m²</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Déclarée : {formatNumber(Math.round(data.surface.buildings_declared_m2))} m² · Somme lots : {formatNumber(Math.round(data.surface.lots_sum_m2))} m²
          </div>
        </div>
        <div className="card-elevated p-6">
          <div className="text-overline">Loyers théoriques</div>
          <div className="mt-2 font-serif text-3xl">{formatEuro(data.occupancy.monthly_theoretical_rent)}<span className="text-sm text-muted-foreground"> /mois</span></div>
          <div className="mt-1 text-xs text-muted-foreground">{formatEuro(data.occupancy.annual_theoretical_rent)} annualisés</div>
        </div>
        <div className="card-elevated p-6">
          <div className="text-overline">Charges déclarées</div>
          <div className="mt-2 font-serif text-3xl">{formatEuro(data.charges.annual_declared)}<span className="text-sm text-muted-foreground"> /an</span></div>
          <div className="mt-1 text-xs text-muted-foreground">{data.charges.count} charge{data.charges.count > 1 ? "s" : ""}</div>
        </div>
      </section>

      <section className="card-elevated p-6" data-testid="patrimoine-data-quality">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <div className="text-overline">Qualité des données</div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <DataQuality label="Lots sans surface" count={data.data_quality.lots_missing_surface} />
          <DataQuality label="Bâtiments sans prix d'achat" count={data.data_quality.buildings_missing_price} />
          <DataQuality label="Bâtiments sans document" count={data.data_quality.buildings_missing_documents} />
        </div>
      </section>
    </div>
  );
}

function FilterSelect({ label, value, onValueChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function DataQuality({ label, count }) {
  const tone = count === 0 ? "emerald" : count < 3 ? "amber" : "red";
  const cls = tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-red-200 bg-red-50 text-red-800";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-xs font-bold uppercase tracking-widest">{label}</div>
      <div className="mt-1 font-serif text-3xl">{count}</div>
    </div>
  );
}
